import type { AppConfig } from '@/types';

// Environment variables with fallbacks
const getEnvVar = (name: string, fallback: string = ''): string => {
  return import.meta.env[name] ?? fallback;
};

const getBooleanEnvVar = (name: string, fallback: boolean = false): boolean => {
  const value = import.meta.env[name];
  if (value === undefined) return fallback;
  return value === 'true' || value === '1';
};

// App configuration
export const appConfig: AppConfig = {
  // In production, prefer explicit env; fallback to custom domain to avoid azurewebsites.net CSP mismatch
  apiBaseUrl: getEnvVar('VITE_API_BASE_URL', (import.meta.env.PROD ? 'https://supportdesk-uat.webcoda.net.au/api/v1' : 'http://localhost:5500/api/v1')),
  apiVersion: 'v1',
  appName: 'SupportDesk',
  version: getEnvVar('VITE_APP_VERSION', '1.5.5'),
  environment: (getEnvVar('VITE_ENVIRONMENT', 'development') as any),
  features: {
    enableDarkMode: getBooleanEnvVar('VITE_FEATURE_DARK_MODE', true),
    enableNotifications: getBooleanEnvVar('VITE_FEATURE_NOTIFICATIONS', true),
    enableLiveChat: getBooleanEnvVar('VITE_FEATURE_LIVE_CHAT', true),
    enableFileAttachments: getBooleanEnvVar('VITE_FEATURE_FILE_ATTACHMENTS', true),
    enableAdvancedSearch: getBooleanEnvVar('VITE_FEATURE_ADVANCED_SEARCH', true),
    enableReporting: getBooleanEnvVar('VITE_FEATURE_REPORTING', false),
    enableWorkflowAutomation: getBooleanEnvVar('VITE_FEATURE_WORKFLOW_AUTOMATION', false),
  },
};

// API endpoints
export const apiEndpoints = {
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    profile: '/auth/profile',
    register: '/auth/register',
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password',
    changePassword: '/auth/change-password',
  },
  users: {
    list: '/users',
    agents: '/users/agents',
    create: '/users',
    get: (id: string) => `/users/${id}`,
    update: (id: string) => `/users/${id}`,
    delete: (id: string) => `/users/${id}`,
    profile: '/users/profile',
    activate: (id: string) => `/users/${id}/activate`,
    deactivate: (id: string) => `/users/${id}/deactivate`,
    resetPassword: (id: string) => `/users/${id}/reset-password`,
    bulkUpdate: '/users/bulk-update',
    bulkDeactivate: '/users/bulk-deactivate',
    statistics: '/users/statistics',
    activity: (id: string) => `/users/${id}/activity`,
    export: '/users/export',
    checkExists: '/users/check-exists',
    invite: '/users/invite',
    acceptInvitation: '/users/accept-invitation',
    profileImage: (id: string) => `/users/${id}/profile-image`,
    permissions: (id: string) => `/users/${id}/permissions`,
    tickets: (id: string) => `/users/${id}/tickets`,
  },
  tickets: {
    list: '/tickets',
    create: '/tickets',
    get: (id: string) => `/tickets/${id}`,
    update: (id: string) => `/tickets/${id}`,
    delete: (id: string) => `/tickets/${id}`,
    updateStatus: (id: string) => `/tickets/${id}/status`,
    updatePriority: (id: string) => `/tickets/${id}/priority`,
    assign: (id: string) => `/tickets/${id}/assign`,
    conversations: (id: string) => `/tickets/${id}/conversations`,
    attachments: (id: string) => `/tickets/${id}/attachments`,
  },
  conversations: {
    create: '/conversations',
    get: (id: string) => `/conversations/${id}`,
    update: (id: string) => `/conversations/${id}`,
    delete: (id: string) => `/conversations/${id}`,
  },
  attachments: {
    upload: '/attachments',
    download: (id: string) => `/attachments/${id}/download`,
    delete: (id: string) => `/attachments/${id}`,
  },
  email: {
    retryOutbound: (id: string) => `/email/outbound/${id}/retry`,
  },
  dashboard: {
    stats: '/dashboard/stats',
    metrics: '/dashboard/metrics',
  },
  analytics: {
    executive: '/analytics/executive/dashboard',
    operational: '/analytics/operational',
    agentPerformance: '/analytics/agents/performance',
    ticketTrends: '/analytics/ticket-trends',
    categoryDistribution: '/analytics/category-distribution',
    csatTrends: '/analytics/csat-trends',
    slaCompliance: '/analytics/sla-compliance',
    teamWorkload: '/analytics/team-workload',
    realtimeDashboard: '/analytics/realtime-dashboard',
    export: '/analytics/export',
  },
  search: {
    global: '/search',
  },
  sla: {
    policies: '/sla/policies',
    policy: (id: string) => `/sla/policies/${id}`,
    escalationRules: '/sla/escalation-rules',
    escalationRule: (id: string) => `/sla/escalation-rules/${id}`,
    businessHours: '/sla/business-hours',
    businessHour: (id: string) => `/sla/business-hours/${id}`,
    dashboard: '/sla/dashboard',
    breaches: '/sla/breaches',
    approaching: '/sla/approaching',
    performance: '/sla/performance',
    validate: '/sla/validate',
  },
  workflows: {
    list: '/workflows',
    create: '/workflows',
    get: (id: string) => `/workflows/${id}`,
    update: (id: string) => `/workflows/${id}`,
    delete: (id: string) => `/workflows/${id}`,
    activate: (id: string) => `/workflows/${id}/activate`,
    test: (id: string) => `/workflows/${id}/test`,
    duplicate: (id: string) => `/workflows/${id}/duplicate`,
    execute: '/workflows/execute',
    executions: (id: string) => `/workflows/${id}/executions`,
    templates: '/workflows/templates',
    createFromTemplate: (templateId: string) => `/workflows/templates/${templateId}/create`,
    export: (id: string) => `/workflows/${id}/export`,
    import: '/workflows/import',
    stats: '/workflows/stats',
    analytics: (id: string) => `/workflows/${id}/analytics`,
    validate: '/workflows/validate',
  },
  companies: {
    list: '/companies',
    create: '/companies',
    get: (id: string) => `/companies/${id}`,
    update: (id: string) => `/companies/${id}`,
    delete: (id: string) => `/companies/${id}`,
  },
  health: '/health',
};

// Local storage keys
export const storageKeys = {
  accessToken: 'supportdesk_access_token',
  refreshToken: 'supportdesk_refresh_token',
  user: 'supportdesk_user',
  theme: 'supportdesk_theme',
  sidebarCollapsed: 'supportdesk_sidebar_collapsed',
  recentTickets: 'supportdesk_recent_tickets',
  preferences: 'supportdesk_preferences',
};

// App constants
export const appConstants = {
  defaultPageSize: 25,
  maxPageSize: 100,
  maxFileSize: 10 * 1024 * 1024, // 10MB
  maxEmailAttachmentTotalSize: 20 * 1024 * 1024, // 20MB before email base64 expansion
  allowedFileTypes: [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
  supportedLanguages: ['en', 'es', 'fr', 'de'],
  defaultLanguage: 'en',
  sessionTimeout: 30 * 60 * 1000, // 30 minutes
  autoSaveInterval: 30 * 1000, // 30 seconds
  notificationDuration: 5000, // 5 seconds
  debounceDelay: 300, // 300ms
  animationDuration: 200, // 200ms
};

// Validation rules
export const validationRules = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  password: {
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: false,
  },
  username: {
    minLength: 3,
    maxLength: 50,
    pattern: /^[a-zA-Z0-9_-]+$/,
  },
  phone: /^\+?[\d\s\-\(\)]+$/,
  ticket: {
    subjectMinLength: 3,
    subjectMaxLength: 200,
    descriptionMinLength: 10,
    descriptionMaxLength: 10000,
  },
};

// Feature flags helper
export const isFeatureEnabled = (feature: keyof typeof appConfig.features): boolean => {
  return appConfig.features[feature] ?? false;
};

// Environment helpers
export const isDevelopment = appConfig.environment === 'development';
export const isProduction = appConfig.environment === 'production';
export const isStaging = appConfig.environment === 'staging';

// Debug mode (only available in development)
export const debugMode = isDevelopment && getBooleanEnvVar('VITE_DEBUG', false);
